"use client"

import { useRef, type ReactNode } from "react"
import { ChevronLeftIcon, ChevronRightIcon } from "@/components/brand-kit/icon-sprite"

interface HorizontalCarouselProps {
  /** Eyebrow text above title */
  eyebrow?: string
  /** Main headline */
  title: string
  /** Supporting description */
  description?: string
  /** Carousel card children */
  children: ReactNode
  /** Gap between cards in Tailwind (defaults to "gap-6") */
  gap?: string
  /** Background color class (defaults to "bg-gray-50") */
  bgClass?: string
  /** Additional classes on the outer section */
  className?: string
}

export function HorizontalCarousel({
  eyebrow,
  title,
  description,
  children,
  gap = "gap-6",
  bgClass = "bg-gray-50",
  className = "",
}: HorizontalCarouselProps) {
  const scrollRef = useRef<HTMLDivElement>(null)

  const scroll = (direction: "left" | "right") => {
    if (!scrollRef.current) return
    const amount = 300
    scrollRef.current.scrollBy({
      left: direction === "left" ? -amount : amount,
      behavior: "smooth",
    })
  }

  const isWhite = bgClass === "bg-white"

  return (
    <section className={`${bgClass} rounded-xl py-8 px-5 md:px-8 mt-10 ${isWhite ? "border border-gray-200" : ""} ${className}`}>
      <div>
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 mb-6">
          <div>
            {eyebrow && (
              <span className="text-[#ff9951] font-bold tracking-widest text-xs mb-2 block uppercase">
                {eyebrow}
              </span>
            )}
            <h2 className="text-2xl md:text-3xl font-black tracking-tighter leading-tight text-gray-900">
              {title}
            </h2>
            {description && (
              <p className="text-gray-500 max-w-md text-sm leading-relaxed">{description}</p>
            )}
          </div>
          <div className="flex gap-3">
            {/* FRIDGE-149 §1 — the buttons hold nothing but a chevron, so
                they need an explicit name (same wording as the PDP's copy).
                The chevron colour is the brand orange — FRIDGE-142, owner. */}
            <button
              type="button"
              onClick={() => scroll("left")}
              aria-label="Scroll left"
              className="w-10 h-10 flex items-center justify-center rounded-lg bg-white hover:bg-gray-100 transition-colors shadow-sm border border-gray-100"
            >
              <ChevronLeftIcon className="w-5 h-5 text-[#ff9951]" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => scroll("right")}
              aria-label="Scroll right"
              className="w-10 h-10 flex items-center justify-center rounded-lg bg-white hover:bg-gray-100 transition-colors shadow-sm border border-gray-100"
            >
              <ChevronRightIcon className="w-5 h-5 text-[#ff9951]" aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* Scroll container */}
        <div
          ref={scrollRef}
          className={`flex overflow-x-auto ${gap} snap-x snap-mandatory pb-4`}
          style={{
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            WebkitOverflowScrolling: "touch",
          }}
        >
          {children}
        </div>
      </div>
    </section>
  )
}
